"use client";
import "reflect-metadata";
import { MotionStack } from "@/commonComponents/motion-components";
import SectionWrapper from "@/commonComponents/section-wrapper";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  arrowImageTransition,
  bottomToToptextTransition,
  formStagger,
  formStaggerChildrenRight,
  formStaggerChildrenUp,
} from "@/commonComponents/animations";
import ContactUsDialog from "@/commonComponents/contactUsDialog/contact-us-dialog";
import ArrowImage from "@/commonComponents/arrow-image";
import { ValidatorFormProps } from "@cc/validation-form";
import {
  IsEmail,
  IsNotEmpty,
  IsNumberString,
  IsOptional,
  IsPhoneNumber,
  Matches,
  ValidateIf,
} from "class-validator";
import InputValidatorsMapper, {
  inputValidationResolver,
} from "@/commonComponents/input-validator-mapper";
import { H1, H6_2 } from "@/styles/theme/components/typography.fontvariant";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import AnimatedButton from "@/commonComponents/animated-button";
import { SubscriptionLike } from "rxjs";
import { I18nLocale } from "@/i18nConfig.server";
import { useCompanyAndCategory } from "@/hooks/contact-us-form.helpers";
import { remove } from "lodash";

export interface ContactUsProps {
  title: string;
  subTitle: string;
  successMsgTitle: string;
  successMsgDesc: string;
  errorMsgTitle: string;
  errorMsgDesc: string;
  image: string;
  locale: I18nLocale;
}

const ContactUsForm = (props: ContactUsProps) => {
  const { title, subTitle, image, locale } = props;
  const [purpose, setPurpose] = useState("General Query");

  class ContactUsFormDTO {
    @IsNotEmpty()
    @Matches(/^[A-Za-z\s]+$/, {
      message: "Name can only contain alphabets and spaces",
    })
    name: string;

    @IsEmail()
    @IsNotEmpty()
    email: string;

    @IsPhoneNumber("IN")
    @IsNotEmpty()
    phone: string;

    @IsNotEmpty()
    @Matches(/^(?![0-9]+$)[a-zA-Z0-9\s]+$/, {
      message: "Invalid organization name",
    })
    organization: string;

    @IsNotEmpty()
    company: string;

    @ValidateIf((o) => !!o.company)
    @IsOptional()
    category: string;

    @IsNotEmpty()
    message: string;

    @IsOptional()
    url: string;

    @ValidateIf(() => purpose === "Partnership" || purpose === "Fundraising")
    @IsNotEmpty()
    @Matches(/^[A-Za-z\s]+$/, { message: "Invalid city" })
    city: string;

    @ValidateIf(() => purpose === "Fundraising")
    @IsNumberString({}, { message: "Amount must be a number" })
    @Matches(/^\d*\.?\d+$|^\d+\.\d*$/, {
      message: "Value must be a positive number",
    })
    @IsNotEmpty()
    amount: string;
  }

  const formMethods = useForm<ContactUsFormDTO>({
    mode: "all",
    delayError: 400,
    criteriaMode: "all",
    reValidateMode: "onChange",
    shouldFocusError: true,
    progressive: true,
    resolver: inputValidationResolver(ContactUsFormDTO),
  });
  const { handleSubmit } = formMethods;
  const {
    setCompany,
    inputFields,
    handleSubmit: onValid,
    isSuccess,
    setIsSuccess,
  } = useCompanyAndCategory({ locale, formMethods });
  remove(inputFields, (item) => item.name === "message");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPurpose((event.target as HTMLInputElement).value);
  };

  useEffect(() => {
    const getFormMethods: (
      formMethods: UseFormReturn<ContactUsFormDTO>,
    ) => Omit<SubscriptionLike, "closed"> | void = (formMethods) => {
      const { watch, resetField } = formMethods;

      return (
        watch &&
        watch((value, { name, type }) => {
          if (name === "company" && type === "change") {
            setCompany(value.company || "");
            resetField("category");
          }
        })
      );
    };

    const formMethodReturn = getFormMethods && getFormMethods(formMethods);

    return () => {
      formMethodReturn?.unsubscribe();
    };
  }, [formMethods, setCompany]);

  const onInvalid = () => console.log("invalid");
  return (
    <SectionWrapper
      SectionProps={{
        sx: {
          background: (theme) => theme.palette.gradient.lightToDark,
        },
      }}
    >
      <Stack
        direction={{ xs: "column", md_lg: "row" }}
        rowGap={3}
        columnGap={5}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <MotionStack
          gap={1}
          variants={bottomToToptextTransition(0)}
          initial="initial"
          whileInView={"animate"}
          viewport={{ once: true }}
          display={{ xs: "flex", md: "none" }}
          textAlign={"center"}
          mb={1}
        >
          <Typography fontSize={H1}>{title}</Typography>
          <Typography fontSize={"18px"}>{subTitle}</Typography>
        </MotionStack>
        {image && (
          <ArrowImage
            src={image}
            alt=""
            width={469}
            height={545}
            sx={{
              width: { xs: 342, xl: 469 },
              minWidth: { xs: 342, xl: 469 },
              height: { xs: 398, xl: 545 },
            }}
            ContainerProps={{ ...arrowImageTransition }}
          />
        )}
        <MotionStack
          rowGap={5}
          maxWidth={{ xs: 1, md_lg: 670 }}
          variants={formStagger}
          initial="initial"
          whileInView={"animate"}
          viewport={{ once: true }}
        >
          <MotionStack
            gap={1}
            variants={formStaggerChildrenRight}
            display={{ xs: "none", md: "flex" }}
          >
            <Typography fontSize={H1}>{title}</Typography>
            <Typography fontSize={"18px"}>{subTitle}</Typography>
          </MotionStack>
          <FormProvider {...formMethods}>
            <MotionStack
              component={"form"}
              onSubmit={handleSubmit(onValid, onInvalid)}
              variants={formStaggerChildrenUp}
            >
              <InputValidatorsMapper
                Dto={ContactUsFormDTO}
                inputFields={inputFields}
                WrapperComponent={Grid2}
                WrapperComponentProps={{
                  container: true,
                  p: 0,
                  columnSpacing: 5,
                  rowSpacing: 3,
                }}
                ItemComponent={Grid2}
                ItemComponentProps={{
                  xs: 12,
                }}
                ButtonItemComponentProps={{
                  xs: 12,
                  md: 6,
                }}
                removeButton={true}
              />
              <Stack rowGap={2} mt={{ xs: 3, md: 4 }} mb={{ xs: 2, md: 3 }}>
                <Typography fontSize={H6_2} color="#F5F5F5">
                  Select Your Purpose
                </Typography>
                <RadioGroup
                  row
                  sx={{ columnGap: 4, rowGap: 2 }}
                  value={purpose}
                  onChange={handleChange}
                >
                  {["General Query", "Partnership", "Fundraising"].map(
                    (item, index) => (
                      <FormControlLabel
                        key={index}
                        value={item}
                        sx={{
                          "*": {
                            color: "#DADADACC",
                          },
                          ".MuiTypography-root": {
                            color: "#DADADACC !important",
                          },
                          ".Mui-checked ~ .MuiTypography-root": {
                            color: "#FFFFFFCC !important",
                          },
                          width: { xs: 1, md: "auto" },
                        }}
                        control={
                          <Radio
                            sx={{
                              py: 0,
                            }}
                          />
                        }
                        label={item}
                      />
                    ),
                  )}
                </RadioGroup>
              </Stack>
              {purpose === "General Query" ? (
                <InputValidatorsMapper
                  Dto={QueryDTO}
                  inputFields={queryInputFields}
                  WrapperComponent={Grid2}
                  WrapperComponentProps={{
                    container: true,
                    p: 0,
                    columnSpacing: 5,
                    rowSpacing: 3,
                  }}
                  ItemComponent={Grid2}
                  ItemComponentProps={{
                    xs: 12,
                  }}
                  ButtonItemComponentProps={{
                    xs: 12,
                    md: 6,
                  }}
                  removeButton={true}
                />
              ) : purpose === "Partnership" ? (
                <InputValidatorsMapper
                  Dto={PartnershipDTO}
                  inputFields={partnershipInputFields}
                  WrapperComponent={Grid2}
                  WrapperComponentProps={{
                    container: true,
                    p: 0,
                    columnSpacing: 5,
                    rowSpacing: 3,
                  }}
                  ItemComponent={Grid2}
                  ItemComponentProps={{
                    xs: 12,
                  }}
                  ButtonItemComponentProps={{
                    xs: 12,
                    md: 6,
                  }}
                  removeButton={true}
                />
              ) : (
                <InputValidatorsMapper
                  Dto={FundraisingDTO}
                  inputFields={fundraisingInputFields}
                  WrapperComponent={Grid2}
                  WrapperComponentProps={{
                    container: true,
                    p: 0,
                    columnSpacing: 5,
                    rowSpacing: 3,
                  }}
                  ItemComponent={Grid2}
                  ItemComponentProps={{
                    xs: 12,
                  }}
                  ButtonItemComponentProps={{
                    xs: 12,
                    md: 6,
                  }}
                  removeButton={true}
                />
              )}
              <AnimatedButton
                type={"submit"}
                rotation="anticlockwise"
                variant={"contained"}
                color={"secondary"}
                sx={{
                  width: { xs: 1, md: 313 },
                  mt: 5,
                }}
              >
                Submit
              </AnimatedButton>
            </MotionStack>
          </FormProvider>
          <ContactUsDialog
            open={isSuccess}
            onClose={() => {
              setIsSuccess(false);
            }}
          />
        </MotionStack>
      </Stack>
    </SectionWrapper>
  );
};

export default ContactUsForm;

class QueryDTO {
  @IsNotEmpty()
  message: string;
}

class PartnershipDTO {
  @IsOptional()
  url: string;
  @IsNotEmpty()
  @Matches(/^[A-Za-z\s]+$/, { message: "Invalid city" })
  city: string;
  @IsNotEmpty()
  message: string;
}

class FundraisingDTO {
  @IsNotEmpty()
  // @IsPositive()
  @Matches(/^\d*\.?\d+$|^\d+\.\d*$/, {
    message: "Value must be a positive number",
  })
  @IsNumberString({}, { message: "Amount must be a number" })
  amount: string;
  @IsNotEmpty()
  @Matches(/^[A-Za-z\s]+$/, { message: "Invalid city" })
  city: string;
  @IsNotEmpty()
  message: string;
}

const queryInputFields: ValidatorFormProps<QueryDTO>["inputFields"] = [
  {
    name: "message",
    placeholder: "Enter A Message",
    multiline: true,
    rows: 4,
  },
];
const partnershipInputFields: ValidatorFormProps<PartnershipDTO>["inputFields"] =
  [
    {
      name: "url",
      placeholder: "Organization URL (optional)",
      ItemComponentProps: {
        md: 6,
      },
    },
    {
      name: "city",
      placeholder: "City",
      ItemComponentProps: {
        md: 6,
      },
    },
    {
      name: "message",
      placeholder: "Enter A Message",
      multiline: true,
      rows: 4,
    },
  ];
const fundraisingInputFields: ValidatorFormProps<FundraisingDTO>["inputFields"] =
  [
    {
      name: "amount",
      placeholder: "Enter Amount To Be Raised",
      ItemComponentProps: {
        md: 6,
      },
    },
    {
      name: "city",
      placeholder: "City",
      ItemComponentProps: {
        md: 6,
      },
    },
    {
      name: "message",
      placeholder: "Enter A Message",
      multiline: true,
      rows: 4,
    },
  ];
